import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Star, ListChecks, Clock, Calendar as CalendarIcon, Archive, CheckCircle2, Search, Plus, Settings as SettingsIcon, Folder, Tag as TagIcon, Menu, X, Layers } from 'lucide-react';
import { api } from '../lib/api';
import { Link, useRouter } from '../lib/router';
import { cn } from '../lib/cn';
import { QuickAdd } from './QuickAdd';
import { TaskListView } from './TaskListView';
import { ProjectView } from './ProjectView';
import { AreasView } from './AreasView';
import { Settings } from './Settings';
import { SearchView } from './SearchView';
import { HelpOverlay } from './HelpOverlay';

type NavKey = 'today' | 'inbox' | 'next' | 'waiting' | 'scheduled' | 'someday' | 'logbook';

export function Shell({ user }: { user: { id: string; email: string; name: string } }) {
  const { path, search, navigate } = useRouter();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (search.get('quick') === '1') {
      setQuickAdd(true);
      const u = new URL(window.location.href);
      u.searchParams.delete('quick');
      window.history.replaceState({}, '', u.toString());
    }
  }, [search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        if (e.key === 'Escape' && target.tagName !== 'TEXTAREA') (target as HTMLInputElement).blur();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/search');
      } else if (e.key === 'n' || (e.metaKey && e.key === 'Enter')) {
        e.preventDefault();
        setQuickAdd(true);
      } else if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setHelpOpen((v) => !v);
      } else if (e.key === 'g') {
        e.preventDefault();
        const goHandler = (ev: KeyboardEvent) => {
          ev.preventDefault();
          const m: Record<string, string> = {
            i: '/inbox', t: '/today', n: '/next', w: '/waiting',
            s: '/scheduled', m: '/someday', l: '/logbook', p: '/projects',
          };
          const to = m[ev.key.toLowerCase()];
          if (to) navigate(to);
          window.removeEventListener('keydown', goHandler);
        };
        window.addEventListener('keydown', goHandler, { once: true });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const route = useMemo(() => {
    const p = path.replace(/^\//, '').replace(/\/$/, '');
    if (!p || p === 'today') return { kind: 'view' as const, name: 'today' as NavKey };
    const navs: NavKey[] = ['inbox', 'next', 'waiting', 'scheduled', 'someday', 'logbook'];
    if (navs.includes(p as NavKey)) return { kind: 'view' as const, name: p as NavKey };
    if (p === 'projects') return { kind: 'projects' as const };
    if (p.startsWith('project/')) return { kind: 'project' as const, id: p.slice('project/'.length) };
    if (p === 'areas') return { kind: 'areas' as const };
    if (p === 'settings') return { kind: 'settings' as const };
    if (p === 'search') return { kind: 'search' as const };
    return { kind: 'view' as const, name: 'today' as NavKey };
  }, [path]);

  return (
    <div className="h-full flex flex-col md:flex-row bg-zinc-950 text-zinc-100">
      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between px-3 pt-safe border-b border-zinc-800 bg-zinc-950/95 backdrop-blur sticky top-0 z-30">
        <button onClick={() => setMobileNavOpen(true)} className="p-2 -ml-2" aria-label="Menu">
          <Menu className="w-5 h-5" />
        </button>
        <div className="font-semibold text-sm">Doer</div>
        <Link to="/search" className="p-2 -mr-2" aria-label="Search">
          <Search className="w-5 h-5" />
        </Link>
      </header>

      <Sidebar
        user={user}
        currentPath={path}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      <main
        className="flex-1 min-w-0 overflow-y-auto md:pb-0"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
      >
        {route.kind === 'view' && <TaskListView key={route.name} viewName={route.name} />}
        {route.kind === 'projects' && <ProjectsList />}
        {route.kind === 'project' && <ProjectView projectId={route.id} />}
        {route.kind === 'areas' && <AreasView />}
        {route.kind === 'settings' && <Settings user={user} />}
        {route.kind === 'search' && <SearchView />}
      </main>

      {/* Floating Quick Add button (mobile + desktop) */}
      <button
        onClick={() => setQuickAdd(true)}
        className={cn(
          'fixed right-4 md:bottom-6 md:right-6 z-40',
          'h-14 w-14 rounded-full bg-amber-500 text-black shadow-lg shadow-amber-500/30 grid place-items-center hover:bg-amber-400 transition',
        )}
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
        aria-label="Quick add"
        title="Quick add (n)"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Mobile bottom nav */}
      <MobileTabs path={path} />

      {quickAdd && <QuickAdd onClose={() => setQuickAdd(false)} />}
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function Sidebar({
  user,
  currentPath,
  mobileOpen,
  onMobileClose,
}: {
  user: { name: string; email: string };
  currentPath: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const stats = useQuery({ queryKey: ['stats'], queryFn: api.stats, refetchInterval: 60_000 });
  const areas = useQuery({ queryKey: ['areas'], queryFn: api.listAreas });
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.listProjects });

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={onMobileClose}>
          <div className="absolute inset-0 bg-black/60" />
          <aside
            className="absolute left-0 top-0 h-full w-72 bg-zinc-950 border-r border-zinc-800 pt-safe animate-pop"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContents
              user={user}
              currentPath={currentPath}
              stats={stats.data}
              areas={areas.data ?? []}
              projects={projects.data ?? []}
              onNavigate={onMobileClose}
              showClose
            />
          </aside>
        </div>
      )}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-zinc-800 bg-zinc-950/80 backdrop-blur">
        <SidebarContents
          user={user}
          currentPath={currentPath}
          stats={stats.data}
          areas={areas.data ?? []}
          projects={projects.data ?? []}
          onNavigate={() => {}}
        />
      </aside>
    </>
  );
}

function SidebarContents({
  user,
  currentPath,
  stats,
  areas,
  projects,
  onNavigate,
  showClose = false,
}: {
  user: { name: string; email: string };
  currentPath: string;
  stats?: Record<string, number>;
  areas: any[];
  projects: any[];
  onNavigate: () => void;
  showClose?: boolean;
}) {
  const items: { to: string; label: string; icon: any; count?: number; hint?: string }[] = [
    { to: '/today', label: 'Today', icon: Star, count: stats?.focus },
    { to: '/inbox', label: 'Inbox', icon: Inbox, count: stats?.inbox },
    { to: '/next', label: 'Next', icon: ListChecks, count: stats?.next },
    { to: '/waiting', label: 'Waiting', icon: Clock, count: stats?.waiting },
    { to: '/scheduled', label: 'Scheduled', icon: CalendarIcon, count: stats?.scheduled },
    { to: '/someday', label: 'Someday', icon: Archive, count: stats?.someday },
    { to: '/logbook', label: 'Logbook', icon: CheckCircle2, count: stats?.done },
  ];
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-amber-500 grid place-items-center text-black font-black">D</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">Doer</div>
          <div className="text-xs text-zinc-500 truncate">{user.name}</div>
        </div>
        {showClose && (
          <button onClick={onNavigate} aria-label="Close" className="p-1.5">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <nav className="px-2 space-y-0.5">
        {items.map((it) => {
          const Icon = it.icon;
          const active = currentPath === it.to;
          return (
            <Link
              key={it.to}
              to={it.to}
              onClick={onNavigate}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm',
                active ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100',
              )}
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1">{it.label}</span>
              {typeof it.count === 'number' && it.count > 0 && (
                <span className="text-xs text-zinc-500 tabular-nums">{it.count}</span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="mt-5 px-4 text-xs uppercase tracking-wider text-zinc-500 flex items-center justify-between">
        <span>Projects</span>
        <Link to="/projects" onClick={onNavigate} className="text-zinc-500 hover:text-zinc-300">All</Link>
      </div>
      <nav className="px-2 mt-1 space-y-0.5 overflow-y-auto max-h-56 lg:max-h-72">
        {projects.filter((p) => p.status !== 'done').slice(0, 24).map((p) => (
          <Link
            key={p.id}
            to={`/project/${p.id}`}
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm',
              currentPath === `/project/${p.id}` ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100',
            )}
          >
            <Folder className="w-3.5 h-3.5 opacity-70" />
            <span className="truncate">{p.name}</span>
          </Link>
        ))}
        {projects.length === 0 && (
          <div className="text-xs text-zinc-600 px-3 py-2">No projects yet.</div>
        )}
      </nav>

      <div className="mt-4 px-4 text-xs uppercase tracking-wider text-zinc-500 flex items-center justify-between">
        <span>Areas</span>
        <Link to="/areas" onClick={onNavigate} className="text-zinc-500 hover:text-zinc-300">Edit</Link>
      </div>
      <nav className="px-2 mt-1 space-y-0.5">
        {areas.map((a) => (
          <Link
            key={a.id}
            to={`/projects?area=${a.id}`}
            onClick={onNavigate}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: a.color || '#52525b' }}
            />
            <span className="truncate">{a.name}</span>
          </Link>
        ))}
      </nav>

      <div className="mt-auto p-3 border-t border-zinc-800 flex items-center gap-2">
        <Link to="/settings" onClick={onNavigate} className="flex-1 flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 px-2 py-1.5 rounded-md hover:bg-zinc-900">
          <SettingsIcon className="w-4 h-4" /> Settings
        </Link>
        <Link to="/search" onClick={onNavigate} className="p-2 rounded-md text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200" aria-label="Search">
          <Search className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function MobileTabs({ path }: { path: string }) {
  const tabs = [
    { to: '/today', label: 'Today', icon: Star },
    { to: '/inbox', label: 'Inbox', icon: Inbox },
    { to: '/next', label: 'Next', icon: ListChecks },
    { to: '/projects', label: 'Projects', icon: Layers },
  ];
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 pb-safe">
      <div className="grid grid-cols-4">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = path === t.to || (t.to === '/today' && path === '/');
          return (
            <Link
              key={t.to}
              to={t.to}
              className={cn(
                'flex flex-col items-center justify-center py-2 text-xs',
                active ? 'text-amber-400' : 'text-zinc-500',
              )}
            >
              <Icon className={cn('w-5 h-5 mb-0.5', active && 'stroke-[2.4]')} />
              {t.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function ProjectsList() {
  const { search } = useRouter();
  const areaFilter = search.get('area');
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.listProjects });
  const areas = useQuery({ queryKey: ['areas'], queryFn: api.listAreas });
  const filtered = (projects.data ?? []).filter((p) => (areaFilter ? p.areaId === areaFilter : true));
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:py-8">
      <h1 className="text-2xl font-semibold tracking-tight mb-1">Projects</h1>
      <p className="text-sm text-zinc-500 mb-4">Multi-step outcomes. Don't pretend they're one task.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.map((p) => {
          const area = areas.data?.find((a) => a.id === p.areaId);
          return (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              className="block rounded-xl border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 p-3"
            >
              <div className="flex items-center gap-2">
                {area && <span className="inline-block h-2 w-2 rounded-full" style={{ background: area.color || '#52525b' }} />}
                <div className="font-medium">{p.name}</div>
              </div>
              {p.notes && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{p.notes}</div>}
              <div className="text-xs mt-2 text-zinc-500 flex items-center gap-2">
                <span className="rounded bg-zinc-800 px-1.5 py-0.5">{p.status}</span>
                {area && <span className="text-zinc-600">{area.name}</span>}
              </div>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center text-zinc-500 mt-12">
          No projects. Just vibes, then.
          <div className="mt-4">
            <NewProjectButton />
          </div>
        </div>
      )}
      {filtered.length > 0 && (
        <div className="mt-6">
          <NewProjectButton />
        </div>
      )}
    </div>
  );
}

function NewProjectButton() {
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const router = useRouter();
  const create = useMutation({
    mutationFn: () => api.createProject({ name: name.trim() }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      setOpen(false);
      setName('');
      router.navigate(`/project/${p.id}`);
    },
  });
  return (
    <>
      <button onClick={() => setOpen(true)} className="rounded-lg bg-amber-500 text-black px-3 py-1.5 text-sm font-medium hover:bg-amber-400">
        + New project
      </button>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60" onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm bg-zinc-900 rounded-2xl border border-zinc-800 p-4 mx-3" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold mb-2">New project</div>
            <input
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 mb-3 outline-none focus:border-amber-500"
              placeholder="Launch the thing"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) create.mutate();
                if (e.key === 'Escape') setOpen(false);
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1.5 text-sm text-zinc-400" onClick={() => setOpen(false)}>Cancel</button>
              <button
                className="px-3 py-1.5 text-sm rounded bg-amber-500 text-black font-medium disabled:opacity-50"
                disabled={!name.trim() || create.isPending}
                onClick={() => create.mutate()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// re-export for organization
export { TagIcon };
